import axios from 'axios'
import { env, EnvType } from '../env'
import { handleSortAndFilter, Headers, makeUrl, Params, toBoolean } from './utils'
import { wcForms } from './static/forms'
import { fetchAllQuotes } from './helpers/fetchAllQuotes'

const {
  facadeBaseUrl, getAllUsers, getAllMembers, getAllPolicies, getAllQuotes, getAllSessions
}: EnvType = env

interface ListResult {
  data: any[]
  total: number
}

const emptyList: ListResult = { data: [], total: 0 }

const logTimed = (resource: string, t0: number, extra?: string) => {
  const ms = (performance.now() - t0).toFixed(0)
  console.log(`[getList] ${resource} — ${ms}ms${extra ? ` (${extra})` : ''}`)
}

export const getList = (headers: Headers) =>
  async (resource: string, params: Params): Promise<ListResult> => {
    const t0 = performance.now()
    console.log(`[getList] ${resource} — start`)

    const { page, perPage } = params.pagination
    const start = (page - 1) * perPage

    const query = {
      sort: JSON.stringify([params.sort.field, params.sort.order]),
      page,
      perPage,
      filter: JSON.stringify(params.filter),
    }

    try {
      switch (resource) {

        case 'employees': {
          const resp = await axios.get(makeUrl(facadeBaseUrl, getAllUsers, query), { headers })
          const structuredData = resp['data']['data']['Users'].map((u: any) => {
            u.id = u.Username
            u['email_verified'] = toBoolean(u['email_verified'])
            if (u['phone_number_verified']) {
              u['phone_number_verified'] = toBoolean(u['phone_number_verified'])
            }
            return u
          })
          const sorted = handleSortAndFilter(structuredData, query)
          logTimed(resource, t0)
          return { data: sorted.slice(start, start + perPage), total: structuredData.length }
        }

        case 'gilders': {
          const resp = await axios.get(makeUrl(facadeBaseUrl, getAllMembers, query), { headers })
          const structuredData = resp['data']['data']['Users'].map((u: any) => {
            u.id = u.Username
            u.email_verified = u.email_verified === 'true'
            return u
          })
          const sorted = handleSortAndFilter(structuredData, query)
          logTimed(resource, t0)
          return { data: sorted.slice(start, start + perPage), total: structuredData.length }
        }

        case 'policies': {
          const resp = await axios.get(makeUrl(facadeBaseUrl, getAllPolicies, query), { headers })
          const data = resp['data']['data']['data']
          const sorted = handleSortAndFilter(data, query)
          logTimed(resource, t0)
          return { data: sorted.slice(start, start + perPage), total: data.length }
        }

        case 'quotes': {
          const allQuotes = await fetchAllQuotes(headers, params.sort, params.filter)
          const sorted = handleSortAndFilter(allQuotes, query)
          logTimed(resource, t0)
          return { data: sorted.slice(start, start + perPage), total: allQuotes.length }
        }

        case 'sessions': {
          const resp = await axios.get(makeUrl(facadeBaseUrl, getAllSessions, query), { headers })
          const structuredData = resp.data['data'].map((s: any) => {
            s.id = s.uid !== '__anon__' ? `${s.uid}_${s.session}` : `_${s.session}`
            if (s.answer && typeof s.answer === 'string') {
              s.answer = s.answer.slice(0, 1).toUpperCase() + s.answer.slice(1)
            }
            return s
          })
          const sorted = handleSortAndFilter(structuredData, query)
          logTimed(resource, t0)
          return { data: sorted.slice(start, start + perPage), total: structuredData.length }
        }

        case 'partners': {
          const partnerQuery = {
            ...query,
            filter: JSON.stringify({ ...params.filter, partner: true }),
          }
          const resp = await axios.get(makeUrl(facadeBaseUrl, getAllSessions, partnerQuery), { headers })
          const structuredData = resp['data']['data'].map((s: any) => {
            s.id = s.uid !== '__anon__' ? `${s.uid}_${s.session}` : `_${s.session}`
            if (s.answer && typeof s.answer === 'string') {
              s.answer = s.answer.slice(0, 1).toUpperCase() + s.answer.slice(1)
            }
            return s
          })
          const sorted = handleSortAndFilter(structuredData, query)
          logTimed(resource, t0)
          return { data: sorted.slice(start, start + perPage), total: structuredData.length }
        }

        case 'forms': {
          const data = wcForms.map((d: any, idx: number) => ({ ...d, id: idx }))
          const sorted = handleSortAndFilter(data, query)
          logTimed(resource, t0)
          return { data: sorted, total: data.length }
        }
      }
    } catch (e) {
      logTimed(resource, t0, 'error')
      console.error(e)
    }

    return emptyList
  }
